import { useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { camelize } from 'humps'
import { useTheme } from '@mui/material/styles'

import { useAppDispatch, useAppSelector } from 'redux/Hook'
import MultiSelectWithSearch from 'components/Shared/MultiSelectWithSearch'
import { AdminUITheme } from 'types/AdminUiThemeMui'
import { ReportingFilterTypes } from 'types/ReportingFilterBuilderContext'
import { trackActions } from 'containers/Helper'
import { FilterItem } from 'types/FilterBuilder'
import { updateFilter } from 'redux/slices/FilterBuilder'
import type { Group as GroupType } from 'types/Groups'

type Props = {
  item: FilterItem
  index: number
  isAutoFocusEnabled: boolean
}

const ParticipantGroups = (props: Props) => {
  const {
    item: { value },
    index,
    isAutoFocusEnabled: enabled,
  } = props
  const { t } = useTranslation()
  const dispatch = useAppDispatch()

  const {
    features: { features: currentFeatures = [] } = {},
    participantGroups: { data: participantGroups = [] },
  } = useAppSelector((state) => state)

  useEffect(() => {
    trackActions('Clicked on Participant group filter option', {
      featureName: 'Overview dashboard filters',
      sectionName: 'Overview (Home) Dashboard page',
      ...currentFeatures?.['segment_analytics_admin']?.enabled,
    })
  }, [])

  const isOptionEqualToValue = (
    option: { id: number },
    value: { id: number },
  ) => {
    return option.id === value.id
  }

  const fetchedOptions = () => {
    const defaultGroups: GroupType[] = participantGroups?.filter(
      (i: any) => i.defaultSegment,
    )
    const defaultGroupKeys: GroupType[] = defaultGroups.map((group: any) => {
      return {
        ...group,
        key: group.name,
      }
    })
    const customGroups: GroupType[] = participantGroups.filter(
      (group: GroupType) => !group.defaultSegment,
    )
    const defaultGroupsSort: GroupType[] = defaultGroupKeys?.sort(
      (a: any, b: any) => {
        return a.id - b.id
      },
    )
    const result: GroupType[] = defaultGroupsSort.map((group: any) => {
      return {
        ...group,
        name: t(`filters.participantGroup.${camelize(group.key)}`),
      }
    })
    return [...result, ...customGroups]
  }

  const handleOnChange = (value: ReportingFilterTypes) => {
    dispatch(
      updateFilter({
        item: { value },
        index,
      }),
    )
  }

  const selectedOptions = () => {
    const options = value?.map((group: any) => ({
      ...group,
      key: group.key || group.name,
      name: group.defaultSegment
        ? t(`filters.participantGroup.${camelize(group.key || group.name)}`)
        : group.name,
    }))
    return options
  }

  const theme: AdminUITheme = useTheme()

  return (
    <MultiSelectWithSearch
      style={theme.GenericTheme.dynamicFilterWrapperWidth}
      isOptionEqualToValue={isOptionEqualToValue}
      selectedValues={selectedOptions()}
      options={fetchedOptions()}
      handleOnChange={handleOnChange}
      defaultOpen={enabled}
      optionKey="name"
      dataTestId="participant_groups"
      labelKey="filters.participantsGroups.label"
      placeholderKey="filters.search"
      isLoaderEnabled={true}
    />
  )
}

export default ParticipantGroups
